import React, { useState, useEffect } from 'react';
import { notionAPI } from '../services/api';
import colors, { shadows, borderRadius } from '../theme';

function NotionConfigPage() {
    const [config, setConfig] = useState(null);
    const [status, setStatus] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [formData, setFormData] = useState({
        notion_database_id: '',
        notion_api_key: '',
        is_active: true,
        sync_frequency: 'hourly',
        auto_assign: true
    });

    const [showApiKey, setShowApiKey] = useState(false);

    useEffect(() => {
        loadData();

        // Auto-refresh sync logs every 5 seconds
        const interval = setInterval(async () => {
            try {
                const logsRes = await notionAPI.getSyncLogs(10);
                setSyncLogs(logsRes.data.logs);

                // Also refresh status to update running syncs
                const statusRes = await notionAPI.getStatus();
                setStatus(statusRes.data.status);
            } catch (err) {
                console.error('Error refreshing sync logs:', err);
            }
        }, 5000); // Refresh every 5 seconds

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [configRes, statusRes, logsRes] = await Promise.all([
                notionAPI.getConfig(),
                notionAPI.getStatus(),
                notionAPI.getSyncLogs(10)
            ]);

            if (configRes.data.configured) {
                setConfig(configRes.data.config);
                setFormData({
                    notion_database_id: configRes.data.config.notion_database_id,
                    notion_api_key: '', // Don't show the encrypted key
                    is_active: configRes.data.config.is_active,
                    sync_frequency: configRes.data.config.sync_frequency,
                    auto_assign: configRes.data.config.auto_assign
                });
            }

            setStatus(statusRes.data.status);
            setSyncLogs(logsRes.data.logs);
        } catch (err) {
            console.error('Error loading Notion data:', err);
            setError('Failed to load Notion configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        try {
            // Validate inputs
            if (!formData.notion_database_id.trim()) {
                setError('Database ID is required');
                setSaving(false);
                return;
            }

            if (!config && !formData.notion_api_key.trim()) {
                setError('API Key is required for initial setup');
                setSaving(false);
                return;
            }

            // Only include API key if it's been changed
            const payload = {
                notion_database_id: formData.notion_database_id,
                is_active: formData.is_active,
                sync_frequency: formData.sync_frequency,
                auto_assign: formData.auto_assign
            };

            if (formData.notion_api_key.trim()) {
                payload.notion_api_key = formData.notion_api_key;
            }

            const response = await notionAPI.saveConfig(payload);

            if (response.data.success) {
                setSuccess('Notion configuration saved successfully!');
                setFormData({ ...formData, notion_api_key: '' }); // Clear API key field
                loadData(); // Reload to get updated config
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setError('');
        setSuccess('');
        setTesting(true);

        try {
            if (!formData.notion_database_id.trim() || !formData.notion_api_key.trim()) {
                setError('Please enter both Database ID and API Key to test connection');
                setTesting(false);
                return;
            }

            // We'll use the save endpoint with a test - if it passes validation, connection works
            setSuccess('Testing connection...');

            const payload = {
                notion_database_id: formData.notion_database_id,
                notion_api_key: formData.notion_api_key,
                is_active: false, // Don't activate during test
                sync_frequency: formData.sync_frequency,
                auto_assign: formData.auto_assign
            };

            await notionAPI.saveConfig(payload);
            setSuccess('✅ Connection successful! Your Notion integration is configured correctly.');

        } catch (err) {
            setError('❌ Connection failed: ' + (err.response?.data?.error || 'Could not connect to Notion'));
        } finally {
            setTesting(false);
        }
    };

    const handleTriggerSync = async () => {
        setError('');
        setSuccess('');
        setSyncing(true);

        try {
            await notionAPI.sync();
            setSuccess('Sync started! Check the sync logs below for progress.');

            // Refresh logs after a delay
            setTimeout(() => {
                loadData();
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to trigger sync');
        } finally {
            setSyncing(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!window.confirm('Are you sure you want to delete the Notion configuration? This will not delete synced knowledge entries.')) {
            return;
        }

        try {
            await notionAPI.deleteConfig();
            setSuccess('Notion configuration deleted successfully');
            setConfig(null);
            setFormData({
                notion_database_id: '',
                notion_api_key: '',
                is_active: true,
                sync_frequency: 'hourly',
                auto_assign: true
            });
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete configuration');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <p>Loading Notion configuration...</p>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', backgroundColor: colors.gray[50], padding: '2rem' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '2rem' }}>
                    <h1 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                        Notion Integration
                    </h1>
                    <p style={{ color: colors.gray[600] }}>
                        Connect your Notion workspace to automatically sync documentation and generate AI-powered quizzes
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: colors.red[50],
                        color: colors.red[600],
                        borderRadius: borderRadius.md,
                        border: `1px solid ${colors.red[600]}`
                    }}>
                        {error}
                    </div>
                )}

                {success && (
                    <div style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: colors.green[100],
                        color: colors.green[700],
                        borderRadius: borderRadius.md,
                        border: `1px solid ${colors.green[500]}`
                    }}>
                        {success}
                    </div>
                )}

                {/* Status Card */}
                {status && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '1.5rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm,
                        marginBottom: '2rem'
                    }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                            Integration Status
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Status
                                </p>
                                <p style={{ fontSize: '1.25rem', fontWeight: '600', color: status.active ? colors.green[500] : colors.gray[400] }}>
                                    {status.configured ? (status.active ? '✅ Active' : '⏸️ Paused') : '❌ Not Configured'}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Synced Entries
                                </p>
                                <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    {status.syncedKnowledgeCount}
                                </p>
                            </div>
                            <div>
                                <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                    Sync Frequency
                                </p>
                                <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    {status.syncFrequency || 'N/A'}
                                </p>
                            </div>
                            {status.latestSync && (
                                <div>
                                    <p style={{ fontSize: '0.875rem', color: colors.gray[600], marginBottom: '0.25rem' }}>
                                        Last Sync
                                    </p>
                                    <p style={{ fontSize: '1rem', fontWeight: '600' }}>
                                        {new Date(status.latestSync.sync_started_at).toLocaleString()}
                                    </p>
                                    <p style={{ fontSize: '0.75rem', color: colors.gray[500] }}>
                                        {status.latestSync.status === 'completed' ? '✅' : '❌'} {status.latestSync.pages_processed} pages
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Configuration Form */}
                <div style={{
                    backgroundColor: colors.white,
                    padding: '2rem',
                    borderRadius: borderRadius.lg,
                    boxShadow: shadows.sm,
                    marginBottom: '2rem'
                }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
                        Configuration
                    </h2>

                    <form onSubmit={handleSaveConfig}>
                        {/* Database ID */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: colors.gray[700]
                            }}>
                                Notion Database ID *
                            </label>
                            <input
                                type="text"
                                value={formData.notion_database_id}
                                onChange={(e) => setFormData({ ...formData, notion_database_id: e.target.value })}
                                placeholder="e.g., 1234567890abcdef1234567890abcdef"
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: `1px solid ${colors.gray[300]}`,
                                    borderRadius: borderRadius.md,
                                    fontSize: '0.875rem',
                                    fontFamily: 'monospace'
                                }}
                            />
                            <div style={{
                                fontSize: '0.75rem',
                                color: colors.gray[600],
                                marginTop: '0.5rem',
                                padding: '0.75rem',
                                backgroundColor: colors.blue[50],
                                borderRadius: borderRadius.sm,
                                border: `1px solid ${colors.blue[200]}`
                            }}>
                                <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: colors.gray[700] }}>
                                    📋 How to find your Database ID:
                                </p>
                                <ol style={{ margin: '0', paddingLeft: '1.25rem', lineHeight: '1.6' }}>
                                    <li>Open your Notion database as a full page</li>
                                    <li>Copy the URL from your browser</li>
                                    <li>The Database ID is the 32-character code after the last "/" and before the "?"</li>
                                </ol>
                                <p style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                    marginTop: '0.5rem',
                                    padding: '0.5rem',
                                    backgroundColor: colors.white,
                                    borderRadius: borderRadius.sm,
                                    color: colors.gray[800],
                                    wordBreak: 'break-all'
                                }}>
                                    Example URL:<br/>
                                    https://notion.so/<strong style={{ backgroundColor: colors.yellow[200] }}>a1b2c3d4e5f67890a1b2c3d4e5f67890</strong>?v=...
                                    <br/><br/>
                                    Database ID to copy: <strong>a1b2c3d4e5f67890a1b2c3d4e5f67890</strong>
                                </p>
                            </div>
                        </div>

                        {/* API Key */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: colors.gray[700]
                            }}>
                                Notion API Key {!config && '*'}
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showApiKey ? 'text' : 'password'}
                                    value={formData.notion_api_key}
                                    onChange={(e) => setFormData({ ...formData, notion_api_key: e.target.value })}
                                    placeholder={config ? 'Leave empty to keep existing key' : 'secret_xxxxxxxxxxxxx'}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        paddingRight: '4rem',
                                        border: `1px solid ${colors.gray[300]}`,
                                        borderRadius: borderRadius.md,
                                        fontSize: '0.875rem'
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowApiKey(!showApiKey)}
                                    style={{
                                        position: 'absolute',
                                        right: '0.5rem',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '0.75rem',
                                        color: colors.gray[600],
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {showApiKey ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            <p style={{ fontSize: '0.75rem', color: colors.gray[600], marginTop: '0.25rem' }}>
                                Get your API key from <a href="https://www.notion.so/my-integrations" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue[500] }}>notion.so/my-integrations</a>
                            </p>
                        </div>

                        {/* Active Toggle */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: colors.gray[700] }}>
                                    Enable automatic syncing
                                </span>
                            </label>
                        </div>

                        {/* Sync Frequency */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                marginBottom: '0.5rem',
                                color: colors.gray[700]
                            }}>
                                Sync Frequency
                            </label>
                            <select
                                value={formData.sync_frequency}
                                onChange={(e) => setFormData({ ...formData, sync_frequency: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: `1px solid ${colors.gray[300]}`,
                                    borderRadius: borderRadius.md,
                                    fontSize: '0.875rem'
                                }}
                            >
                                <option value="hourly">Hourly</option>
                                <option value="daily">Daily</option>
                                <option value="manual">Manual Only</option>
                            </select>
                        </div>

                        {/* Auto Assign */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.auto_assign}
                                    onChange={(e) => setFormData({ ...formData, auto_assign: e.target.checked })}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: colors.gray[700] }}>
                                    Auto-assign synced content to all users
                                </span>
                            </label>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <button
                                type="submit"
                                disabled={saving}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: saving ? colors.gray[400] : colors.blue[500],
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: borderRadius.md,
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: saving ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </button>

                            <button
                                type="button"
                                onClick={handleTestConnection}
                                disabled={testing}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: colors.white,
                                    color: colors.gray[700],
                                    border: `1px solid ${colors.gray[300]}`,
                                    borderRadius: borderRadius.md,
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    cursor: testing ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {testing ? 'Testing...' : 'Test Connection'}
                            </button>

                            {config && (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleTriggerSync}
                                        disabled={syncing}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: syncing ? colors.gray[400] : colors.green[500],
                                            color: colors.white,
                                            border: 'none',
                                            borderRadius: borderRadius.md,
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: syncing ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {syncing ? 'Syncing...' : 'Trigger Sync Now'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleDeleteConfig}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            backgroundColor: colors.white,
                                            color: colors.red[600],
                                            border: `1px solid ${colors.red[600]}`,
                                            borderRadius: borderRadius.md,
                                            fontSize: '0.875rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            marginLeft: 'auto'
                                        }}
                                    >
                                        Delete Configuration
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>

                {/* Sync Logs */}
                {syncLogs.length > 0 && (
                    <div style={{
                        backgroundColor: colors.white,
                        padding: '2rem',
                        borderRadius: borderRadius.lg,
                        boxShadow: shadows.sm
                    }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                            Recent Sync Logs
                        </h2>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ borderBottom: `2px solid ${colors.gray[200]}` }}>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Started</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Status</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Processed</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Created</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Updated</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Failed</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Duration</th>
                                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: colors.gray[700] }}>Errors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {syncLogs.map((log) => (
                                        <tr key={log.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                                {new Date(log.sync_started_at).toLocaleString()}
                                            </td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: borderRadius.sm,
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    backgroundColor: log.status === 'completed' ? colors.green[100] : log.status === 'running' ? colors.blue[100] : colors.red[50],
                                                    color: log.status === 'completed' ? colors.green[700] : log.status === 'running' ? colors.blue[700] : colors.red[600]
                                                }}>
                                                    {log.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.pages_processed}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.green[600] }}>{log.pages_created}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.blue[600] }}>{log.pages_updated}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: colors.red[600] }}>{log.pages_failed}</td>
                                            <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{log.duration_seconds}s</td>
                                            <td style={{
                                                padding: '0.75rem',
                                                fontSize: '0.75rem',
                                                color: colors.red[600],
                                                maxWidth: '300px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {log.error_details ? (
                                                    <span title={JSON.stringify(log.error_details, null, 2)}>
                                                        {Array.isArray(log.error_details)
                                                            ? log.error_details.map(e => e.error || e.pageId).join(', ')
                                                            : typeof log.error_details === 'string'
                                                                ? log.error_details
                                                                : JSON.stringify(log.error_details)
                                                        }
                                                    </span>
                                                ) : (
                                                    log.pages_failed > 0 ? 'See details' : '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default NotionConfigPage;
